import React, { useState, useEffect } from 'react';
import { useStore } from '@/store/useStore';
import api from '@/lib/api';
import { 
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Loader2,
  Droplets,
  Flame
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  Legend
} from 'recharts';
import { cn } from '@/lib/utils';

const formatNumber = (num) => {
  if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toFixed(0) || '0';
};

const formatCurrency = (num) => {
  if (num >= 1000000000) return `$${(num / 1000000000).toFixed(2)}B`;
  if (num >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num?.toFixed(0) || '0'}`;
};

const Economics = () => {
  const { operators, priceDeck, setPriceDeck } = useStore();
  const [prices, setPrices] = useState([]);
  const [sensitivity, setSensitivity] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [pricesRes, sensitivityRes] = await Promise.all([
          api.get('/economics/prices'),
          api.get(`/economics/sensitivity${selectedOperator !== 'all' ? `?operator_id=${selectedOperator}` : ''}`)
        ]);
        setPrices(pricesRes.data.slice(-90)); // Last 90 days
        setSensitivity(sensitivityRes.data);
      } catch (error) {
        console.error('Error fetching economics data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedOperator]);

  const priceChartData = prices.map(p => ({
    date: p.date.slice(5), // MM-DD format
    wti: p.wti_price,
    henry_hub: p.henry_hub_price * 20, // Scale for visualization
  }));

  const sensitivityChartData = sensitivity?.sensitivity.map(s => ({
    price: `$${s.wti_price}`,
    monthly: s.monthly_revenue,
    annual: s.annual_revenue,
    change: s.change_from_base,
  })) || [];

  const currentPriceData = sensitivity?.sensitivity.find(s => s.wti_price === priceDeck);

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-heading font-bold tracking-wide uppercase">
            Economics
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Revenue & price sensitivity
          </p>
        </div>
        <Select value={selectedOperator} onValueChange={setSelectedOperator}>
          <SelectTrigger 
            data-testid="economics-operator-select"
            className="w-full sm:w-48 lg:w-64 bg-surface border-white/10 h-8 text-xs"
          >
            <SelectValue placeholder="Select operator" />
          </SelectTrigger>
          <SelectContent className="bg-surface border-white/10 max-h-80">
            <SelectItem value="all" className="text-xs">All Operators (Basin)</SelectItem>
            {operators?.map(op => (
              <SelectItem key={op.id} value={op.id} className="text-xs">{op.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Price Deck Slider */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
              WTI Price Deck
            </CardTitle>
            <Badge variant="outline" className="border-primary/50 text-primary font-mono text-sm sm:text-lg px-2 sm:px-4">
              ${priceDeck}/bbl
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-6">
          <div className="space-y-2 sm:space-y-4">
            <Slider
              value={[priceDeck]}
              onValueChange={([val]) => setPriceDeck(val)}
              min={50}
              max={120}
              step={5}
              data-testid="price-deck-slider"
              className="w-full"
            />
            <div className="flex justify-between text-[9px] sm:text-xs font-mono text-muted-foreground">
              <span>$50</span>
              <span>$70</span>
              <span>$90</span>
              <span>$120</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <Droplets className="w-3.5 h-3.5 text-primary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Monthly Prod</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : formatNumber(sensitivity?.monthly_oil_production || 0)}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">BBL/mo</p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <DollarSign className="w-3.5 h-3.5 text-success" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Monthly Rev</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold text-success">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : formatCurrency(currentPriceData?.monthly_revenue || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              <BarChart3 className="w-3.5 h-3.5 text-secondary" />
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">Annual Rev</span>
            </div>
            <p className="text-lg sm:text-2xl font-heading font-bold text-secondary">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : formatCurrency(currentPriceData?.annual_revenue || 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-surface border-white/10">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
              {(currentPriceData?.change_from_base || 0) >= 0 ? (
                <TrendingUp className="w-3.5 h-3.5 text-success" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5 text-danger" />
              )}
              <span className="text-[10px] sm:text-xs uppercase tracking-wider">vs $75</span>
            </div>
            <p className={cn(
              "text-lg sm:text-2xl font-heading font-bold",
              (currentPriceData?.change_from_base || 0) >= 0 ? "text-success" : "text-danger"
            )}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                `${(currentPriceData?.change_from_base || 0) >= 0 ? '+' : ''}${(currentPriceData?.change_from_base || 0).toFixed(1)}%`
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
        {/* Price History */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
              Prices (90 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            {loading ? (
              <div className="h-[200px] sm:h-[300px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="h-[200px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      interval={14}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      domain={[50, 100]}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F0F10', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '12px'
                      }}
                      formatter={(value, name) => [
                        name === 'wti' ? `$${value.toFixed(2)}` : `$${(value / 20).toFixed(2)}`,
                        name === 'wti' ? 'WTI' : 'Henry Hub'
                      ]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="wti" 
                      name="WTI"
                      stroke="#F59E0B" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="henry_hub" 
                      name="Henry Hub (scaled)"
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sensitivity Analysis */}
        <Card className="bg-surface border-white/10">
          <CardHeader className="border-b border-white/5 p-2.5 sm:p-4 bg-white/[0.02]">
            <CardTitle className="text-xs sm:text-sm font-heading uppercase tracking-widest">
              Price Sensitivity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4">
            {loading ? (
              <div className="h-[200px] sm:h-[300px] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="h-[200px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sensitivityChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis 
                      dataKey="price" 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                    />
                    <YAxis 
                      stroke="#52525B" 
                      tick={{ fill: '#A1A1AA', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
                      tickFormatter={(v) => formatCurrency(v)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F0F10', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        fontFamily: 'IBM Plex Mono',
                        fontSize: '12px'
                      }}
                      formatter={(value, name) => [formatCurrency(value), name === 'monthly' ? 'Monthly' : 'Annual']}
                    />
                    <Bar dataKey="monthly" name="Monthly Revenue" radius={[4, 4, 0, 0]}>
                      {sensitivityChartData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.price === `$${priceDeck}` ? '#10B981' : '#3B82F6'}
                          fillOpacity={entry.price === `$${priceDeck}` ? 1 : 0.6}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sensitivity Table */}
      <Card className="bg-surface border-white/10">
        <CardHeader className="border-b border-white/5 p-4 bg-white/[0.02]">
          <CardTitle className="text-sm font-heading uppercase tracking-widest">
            Revenue Sensitivity Matrix
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground uppercase">WTI Price</TableHead>
                <TableHead className="text-xs text-muted-foreground uppercase text-right">Monthly Revenue</TableHead>
                <TableHead className="text-xs text-muted-foreground uppercase text-right">Annual Revenue</TableHead>
                <TableHead className="text-xs text-muted-foreground uppercase text-right">vs Base ($75)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sensitivity?.sensitivity.map((s, idx) => (
                <TableRow 
                  key={idx} 
                  className={cn(
                    "border-white/5",
                    s.wti_price === priceDeck && "bg-primary/10"
                  )}
                >
                  <TableCell className="font-mono">
                    ${s.wti_price}/bbl
                    {s.wti_price === priceDeck && (
                      <Badge variant="outline" className="ml-2 border-primary/50 text-primary text-[10px]">
                        SELECTED
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-right">
                    {formatCurrency(s.monthly_revenue)}
                  </TableCell>
                  <TableCell className="font-mono text-right">
                    {formatCurrency(s.annual_revenue)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn(
                      "font-mono",
                      s.change_from_base >= 0 ? "text-success" : "text-danger"
                    )}>
                      {s.change_from_base >= 0 ? '+' : ''}{s.change_from_base.toFixed(1)}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Economics;
